import React, { JSX } from "react";
import { Link } from "react-router-dom";
import { DashboardScope } from "../mockData";

const scopeIcons: Record<string, JSX.Element> = {
  "Service Complaints": (
    <svg
      className="w-7 h-7 text-blue-500"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 17v-2a4 4 0 018 0v2M5 10a4 4 0 018 0v2m-8 4v-2a4 4 0 018 0v2"
      />
    </svg>
  ),
  "Service Appeals": (
    <svg
      className="w-7 h-7 text-amber-500"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
    </svg>
  ),
  "Prosecutor Complaints": (
    <svg
      className="w-7 h-7 text-rose-500"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
    </svg>
  ),
  "Prosecutor Appeals": (
    <svg
      className="w-7 h-7 text-purple-500"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 2v4M8 2v4M3 10h18"
      />
    </svg>
  ),
  "Feedback & Reviews": (
    <svg
      className="w-7 h-7 text-cyan-500"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
};

type Props = {
  scopes: DashboardScope[];
};

const DashboardScopeGrid: React.FC<Props> = ({ scopes }) => {
  return (
    <section className="rounded-xl bg-white shadow-lg flex flex-col h-full">
      <div className="border-b border-gray-100 px-6 py-5">
        <h2 className="text-xl font-bold text-black flex items-center gap-2">
          <svg
            className="w-6 h-6 text-black"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16 2v4M8 2v4M3 10h18"
            />
          </svg>
          Categories
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-2 flex-1">
        {scopes.map((scope) => (
          <Link
            key={scope.title}
            to={scope.path}
            className="group rounded-xl border border-gray-100 bg-white p-5 transition-all duration-200 hover:shadow-md hover:-translate-y-1 flex flex-col h-full"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  {scopeIcons[scope.title] || null}
                  <h3 className="text-base font-bold text-black group-hover:text-gray-700">
                    {scope.title}
                  </h3>
                </div>
                <p className="mt-2 text-sm leading-5 text-gray-700">
                  {scope.description}
                </p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-extrabold text-black">
                  {scope.total}
                </p>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Total
                </p>
              </div>
              <div>
                <p className="text-2xl font-extrabold text-amber-600">
                  {scope.pending}
                </p>
                <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">
                  Pending
                </p>
              </div>
              <div>
                <p className="text-2xl font-extrabold text-black">
                  {scope.inProgress}
                </p>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  In Progress
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default DashboardScopeGrid;
